"use client";
import { cn } from "@/lib/utils";

import PaginationControls from "./pagination";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "./table";

interface DataTableProps<T> {
  headers: { style?: string; title: string; key: string }[];
  content: T[];
  rowClassName?: string;
  pagination?: boolean;
  total?: number;
  page?: number;
  pageSize?: number;
  onPageChange?: (page: number) => void;
  onPageSizeChange?: (size: number) => void;
  loading?: boolean;
  totalPages?: number;
  showHeader?: boolean;
}

export function DataTable<T>({
  headers,
  content,
  pagination,
  page,
  totalPages,
  onPageChange,
  rowClassName,
  showHeader = true,
}: DataTableProps<T>) {
  const currentPage = Math.max(1, page ?? 1);
  const pagesTotal = Math.max(0, totalPages ?? 0);

  // Helper to extract width and minWidth from tailwind classes w-[...] or min-w-[...] (px or %)
  const extractWidth = (styleStr?: string) => {
    if (!styleStr) return undefined;
    const match = styleStr.match(/(?:^|\s)w-\[(\d+)(px|%)\]/);
    return match ? `${match[1]}${match[2]}` : undefined;
  };

  const extractMinWidth = (styleStr?: string) => {
    if (!styleStr) return undefined;
    const match = styleStr.match(/(?:^|\s)min-w-\[(\d+)(px|%)\]/);
    return match ? `${match[1]}${match[2]}` : undefined;
  };

  // header.style often carries width and text alignment meant only for the
  // real <table> layout (e.g. "w-[30%]" or "text-center"). Below `lg` the row renders as a
  // stacked block card instead, so we scope bare tokens to `lg:` so they only
  // affect the desktop table, leaving mobile cards cleanly left-aligned.
  const scopeStyleToDesktop = (styleStr?: string) => {
    if (!styleStr) return styleStr;
    return styleStr
      .split(/\s+/)
      .filter(Boolean)
      .map((token) => {
        if (/^[a-z0-9]+:/.test(token)) return token;
        if (
          /^(min-|max-)?w-/.test(token) ||
          /^text-(left|center|right|start|end|justify)/.test(token) ||
          /^justify-/.test(token) ||
          /^items-/.test(token)
        ) {
          return `lg:${token}`;
        }
        return token;
      })
      .join(" ");
  };

  return (
    <div className="w-full overflow-x-auto">
      <Table noScrollWrapper className="min-w-full border-collapse lg:table block lg:table-fixed">
        {/* Desktop header: hidden below lg (tablets get the stacked card view) */}
        {showHeader && (
          <TableHeader className="hidden lg:table-header-group">
            <TableRow>
              {headers.map((header, index) => {
                const width = extractWidth(header.style);
                const minWidth = extractMinWidth(header.style) || width;
                return (
                  <TableHead
                    key={`${header?.key ? String(header.key) : ""}-${index}`}
                    className={cn(
                      "font-bold",
                      scopeStyleToDesktop(header.style) ?? "text-start",
                    )}
                    style={{
                      ...(width ? { width, maxWidth: width } : {}),
                      ...(minWidth ? { minWidth } : {}),
                    }}
                  >
                    {header.title}
                  </TableHead>
                );
              })}
            </TableRow>
          </TableHeader>
        )}
        <TableBody className="block lg:table-row-group">
          {content?.map((row, rowIndex) => (
            <TableRow
              key={rowIndex}
              className={cn(
                "text-start flex flex-col lg:table-row mb-4 lg:mb-0 border rounded-lg lg:border-0 lg:rounded-none overflow-hidden transition-all shadow-sm lg:shadow-none",
                rowClassName,
                rowIndex % 2 === 0
                  ? "bg-white "
                  : "bg-gray-50/50 lg:bg-gray-50 dark:bg-gray-900/40",
              )}
            >
              {headers.map((header, colIdx) => {
                const isActions =
                  header.key?.toLowerCase().includes("action") ||
                  header.key?.toLowerCase().includes("accion");
                const width = extractWidth(header.style);
                const minWidth = extractMinWidth(header.style) || width;
                return (
                  <TableCell
                    key={`${rowIndex}-${colIdx}`}
                    // Column widths only apply to the desktop table; the stacked
                    // mobile card must span the full row width.
                    style={
                      {
                        ...(width ? { "--dt-col-w": width } : {}),
                        ...(minWidth ? { "--dt-col-min-w": minWidth } : {}),
                      } as React.CSSProperties
                    }
                    className={cn(
                      "block lg:table-cell lg:align-middle align-top border-b lg:border-b-0 py-3 px-3 text-start",
                      "min-w-0 whitespace-normal break-words",
                      width && "lg:w-(--dt-col-w) lg:max-w-(--dt-col-w)",
                      minWidth && "lg:min-w-(--dt-col-min-w)",
                      scopeStyleToDesktop(header.style),
                      isActions &&
                        "order-last lg:order-none border-t border-b-0 lg:border-t-0 lg:border-b-0",
                    )}
                  >
                    {/* Mobile Label: Only show if header has a title */}
                    {header.title && (
                      <span className="lg:hidden block font-bold text-sm mb-1 text-slate-800 text-start">
                        {header.title}
                      </span>
                    )}
                    <div
                      className={cn(
                        "min-w-0 whitespace-normal break-words text-sm text-gray-700 dark:text-gray-200 text-start",
                        header.style?.includes("text-center")
                          ? "lg:text-center"
                          : header.style?.includes("text-end")
                            ? "lg:text-end"
                            : "lg:text-start",
                        isActions &&
                          "w-full flex items-center justify-start lg:justify-end",
                      )}
                    >
                      {(row as Record<string, unknown>)[
                        header.key
                      ] as React.ReactNode}
                    </div>
                  </TableCell>
                );
              })}
            </TableRow>
          ))}
          {(!content || content.length === 0) && (
            <TableRow>
              <TableCell
                colSpan={headers.length}
                className="text-center py-10 text-gray-500"
              >
                No se encontraron resultados
              </TableCell>
            </TableRow>
          )}
        </TableBody>
      </Table>

      {pagination && pagesTotal > 1 && (
        <div className="w-full mt-6 flex flex-col sm:flex-row items-center justify-end gap-4 border-t pt-4">
          <PaginationControls
            currentPage={currentPage - 1}
            totalPages={pagesTotal}
            onPageChange={(p) => {
              window.scrollTo({ top: 0, behavior: "smooth" });
              if (onPageChange) onPageChange(p + 1);
            }}
          />
        </div>
      )}
    </div>
  );
}
